import { fromPairs, uniqBy } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { useSelector } from 'react-redux';
import { Currency } from 'api/account';
import { ProjectId } from 'api/models';
import { ProjectAPI } from 'api/project';
import { ObjectType } from 'api/pushes';
import { Field } from 'api/tasks';
import { currencyRenderer } from 'components/atoms/currency-renderer';
import { formatterWithMinutes } from 'components/atoms/input-duration';
import { PLACEHOLDER_DEEP_VARIABLE_SUFFIX } from 'components/organisms/automations/store/constant';
import { FieldDataType } from 'components/organisms/module-settings/fields/types';
import {
  FieldType,
  FilterableField,
  SourceType,
  filterableFieldType,
} from 'components/organisms/tasks/fields';
import { TemplateObjectType } from 'data/templates/api';
import { getActiveTemplatePlaceholders } from 'data/templates/projects/selectors';
import { useGetAllTeamsFromIndexedDbConditionally } from 'hooks/use-idb';
import { useTranslate } from 'hooks/use-translation';
import {
  useEnabledProjectFieldsQuery,
  useTeamMemberFieldsQuery,
} from 'queries';
import { tryGetTextFromHTML } from 'utils/getTextFromHTML';
import { pascalCase } from 'utils/pascal-case';
import { useNativeUsersQuery } from './members';
import { useProjectDetailQuery } from './project';

const getProjectVariablesKey = (projectId?: number): string =>
  `project_${projectId}_variables`;

type ProjectVariables = {
  [key: string]: any;
};

const nativeMemberFieldsForMultiUser = [
  'user_id',
  'full_name',
  'first_name',
  'last_name',
  'email_id',
];

/**
 * Ideally should be retrieved from BE alike project and task fields.
 * hardcoding in FE for now for listing along with custom member fields
 * for project field of type USER in simple variables popover.
 */
export const useNativeMemberFieldsOptions = () => {
  const fields = [...nativeMemberFieldsForMultiUser, 'role'];

  return fields.map((_field) => {
    const phrases = _field.split('_');
    const capitalised = phrases.map((_phrase) => pascalCase(_phrase));

    return {
      id: _field,
      label: capitalised.join(' '),
      value: `${phrases.shift()}${pascalCase(phrases.join(''))}`,
    };
  });
};

export const useCalculatedProjectFields = () => {
  const fields = [
    'current_phases',
    'completion_percentage',
    'actual_start_date',
    'actual_completed_date',
    'project_tracked_hours',
    'total_billable_hours',
    'overall_rating',
    'last_milestone_rating',
    'project_age',
    'planned_duration',
    'completed_tasks',
    'overdue_tasks',
    'blocked_tasks',
    'atRisk_tasks',
    'approval_pending',
    'customer_company_name',
  ];

  return fields.map((_field) => {
    const phrases = _field.split('_');
    const capitalised = phrases.map((_phrase) => pascalCase(_phrase));

    return {
      fieldId: _field,
      fieldLabel:
        _field === 'customer_company_name'
          ? 'Customer company'
          : capitalised.join(' '),
      fieldName: `${phrases.shift()}${phrases.map(pascalCase).join('')}`,
      objectType: SourceType.Project?.toUpperCase(),
    };
  });
};

export const useProjectVariablesQuery = (
  projectId?: number,
  queryOptions: UseQueryOptions<
    ProjectVariables,
    unknown,
    ProjectVariables,
    string
  > = {},
): UseQueryResult<ProjectVariables> =>
  useQuery(
    getProjectVariablesKey(projectId),
    () => {
      if (!projectId) {
        return Promise.resolve();
      }

      return ProjectAPI.getVariablesData(projectId);
    },
    {
      ...queryOptions,
      enabled: !!projectId,
      refetchOnMount: true,
    },
  );

// TODO: to have separate hook for project and template.
export const useVariablesMentionData = ({
  projectId,
  templateType,
  fields,
  nestedFields,
}: {
  projectId?: number;
  templateType?: TemplateObjectType;
  fields?: Field[];
  nestedFields?: Field[];
}) => {
  const translate = useTranslate();
  const projectIdMemo = useMemo(() => ProjectId(projectId ?? 0), [projectId]);
  const { data: projectDetail } = useProjectDetailQuery(projectIdMemo, {
    enabled: projectId != null,
  });
  const { data: projectFields } = useEnabledProjectFieldsQuery();
  const calculatedProjectFieldOptions = useCalculatedProjectFields();
  const realTimeVariablesResponse = useProjectVariablesQuery(projectId);
  const { data: teamMemberFields } = useTeamMemberFieldsQuery();
  const nativeMemberFieldsOptions = useNativeMemberFieldsOptions();

  // this fetches account level placeholders and project level placeholders if projectId is passed.
  const _placeholders = useGetAllTeamsFromIndexedDbConditionally(projectId);

  const projectTemplatePlaceholders = useSelector(
    getActiveTemplatePlaceholders(),
  );

  const targetPlaceholders = useMemo(() => {
    if (templateType === TemplateObjectType.Project) {
      return uniqBy(
        [..._placeholders, ...Object.values(projectTemplatePlaceholders)],
        'teamId',
      );
    }

    return _placeholders;
  }, [_placeholders, projectTemplatePlaceholders, templateType]);

  const nativeMemberFieldsOptionsForMultiUserField = useMemo(
    () =>
      nativeMemberFieldsOptions.filter((_option) =>
        nativeMemberFieldsForMultiUser.includes(_option.id),
      ),
    [nativeMemberFieldsOptions],
  );

  const realTimeData = useMemo(
    () =>
      realTimeVariablesResponse ? realTimeVariablesResponse.data?.project : {},
    [realTimeVariablesResponse],
  );

  const addCurrencyPrefix = (value?: any, currency?: Currency): any => {
    if (value == null || currency == null) {
      return value;
    }

    return currencyRenderer(value, currency);
  };

  const getRealTimeValue = useCallback(
    (_field) => {
      if (
        _field.fieldDataType === 'USER' ||
        _field?.objectType === ObjectType.Form
      ) {
        return '';
      }
      let _value;

      if (_field.customField) {
        _value = realTimeData?.customField?.[_field.fieldId];
      } else {
        switch (_field.fieldName) {
          case 'annualizedRecurringRevenue':
            _value = addCurrencyPrefix(
              realTimeData?.arr,
              projectDetail?.projectFinancialsDetailModel?.currency,
            );
            break;
          case 'projectFee':
            _value = addCurrencyPrefix(
              realTimeData?.projectFee,
              projectDetail?.projectFinancialsDetailModel?.currency,
            );
            break;
          case 'customerCompanyName':
            _value = realTimeData?.customer?.companyName;
            break;
          case 'budgetedHours':
            _value = formatterWithMinutes(realTimeData?.budgetedMinutes ?? 0);
            break;
          case 'totalBillableHours':
            _value =
              realTimeData?.totalBillableHoursType === 'minutes'
                ? formatterWithMinutes(realTimeData?.totalBillableHours ?? 0)
                : realTimeData?.totalBillableHours;
            break;
          default:
            _value = realTimeData?.[_field.fieldName];
        }
      }

      return (_field.fieldDataType === FieldType.Boolean.toUpperCase() &&
        _value !== undefined) ||
        _value === 0
        ? `${_value}`
        : _value;
    },
    [projectDetail?.projectFinancialsDetailModel?.currency, realTimeData],
  );

  const getRealTimeValueForNestedField = useCallback(
    (parent, nestedFieldName) => {
      const _parent = (() => {
        if (parent.customField) {
          return realTimeData?.customField?.[parent.fieldId];
        }

        return parent.fieldName === 'projectOwner'
          ? realTimeData?.owner
          : realTimeData?.[parent.fieldName];
      })();

      const _value =
        nestedFieldName === 'role'
          ? _parent?.role?.roleName
          : _parent?.[nestedFieldName];

      return parent.fieldDataType === FieldType.Boolean.toUpperCase() &&
        _value !== undefined
        ? `${_value}`
        : _value;
    },
    [realTimeData],
  );

  const getRealTimeValueForNestedCustomField = useCallback(
    (parent, nestedField: Field) => {
      const _parent = (() => {
        if (parent.customField) {
          return realTimeData?.customField?.[parent.fieldId];
        }

        return parent.fieldName === 'projectOwner'
          ? realTimeData?.owner
          : realTimeData?.[parent.fieldName];
      })();

      const _value = nestedField.customField
        ? _parent?.customField?.[nestedField.fieldId]
        : _parent?.[nestedField.fieldName];

      const finalValue = (() => {
        if (_value === undefined) {
          return _value;
        }
        if (parent.fieldDataType === FieldType.Boolean.toUpperCase()) {
          return `${_value}`;
        }
        if (nestedField.fieldDataType === 'USER') {
          /**
           * BE returns entire user object for custom field of type User.
           * To avoid infinite recursion, we are restricting at the first level
           * in both FE and BE, hence reading full name here
           * for real time replacement.
           */
          return _value.fullName;
        }

        return _value;
      })();

      return finalValue;
    },
    [realTimeData],
  );

  const getRealTimeValueForPlaceholderNativeField = useCallback(
    (_team, nestedFieldName) => {
      const _parent = realTimeData?.placeholder?.[_team.teamId];

      return nestedFieldName === 'role'
        ? _parent?.role?.roleName
        : _parent?.[nestedFieldName];
    },
    [realTimeData],
  );

  const getRealTimeValueForPlaceholderCustomField = useCallback(
    (_team, _field: Field) => {
      const _parent = realTimeData?.placeholder?.[_team.teamId];

      return _field.customField
        ? _parent?.customField?.[_field.fieldId]
        : _parent?.[_field.fieldName];
    },
    [realTimeData],
  );
  const getFormFieldOptions = useCallback(
    (_field, index, parentFEValue, parentBEValue) => {
      // Check if the field has FORM objectType
      const isFormObjectType = _field.objectType.toString() === ObjectType.Form;

      const formFields =
        nestedFields?.filter(
          (field) => field.objectType?.toString() === ObjectType.Form,
        ) ?? [];

      return isFormObjectType
        ? {
            nestedOptions: formFields.map((_option, _index) => ({
              objectType: _field.objectType,
              id: `${_field.fieldId}_${_option.createdAt}`,
              indexString: `${index}.${_index}`,
              label: _option.fieldLabel,
              value: `${parentFEValue} → ${_option.fieldLabel}`,
              backendValue:
                _option.fieldName !== ''
                  ? `${parentBEValue}${PLACEHOLDER_DEEP_VARIABLE_SUFFIX}.${_option.fieldName}`
                  : `${parentBEValue}`,
              realTimeValue: getRealTimeValueForNestedField(
                _field,
                _option.fieldName,
              ),
            })),
          }
        : {};
    },
    [nestedFields, getRealTimeValueForNestedField],
  );

  const getUserFieldOptions = useCallback(
    (_field, index, parentFEValue, parentBEValue) => {
      const isUserDataType =
        (_field.fieldDataType === 'USER' ||
          /**
           * since BE returns project owner as number field,
           * whereas it should be treated as user data type.
           */
          _field.fieldName === 'projectOwner') &&
        !_field?.fieldName?.includes('assignee');

      const isMultiSelectUserField =
        isUserDataType && _field.fieldType === 'MULTI_SELECT';

      const _backendValue = (_option: {
        id: string;
        label: string;
        value: string;
      }) =>
        _option.value === 'role'
          ? `${parentBEValue}.role.roleName`
          : `${parentBEValue}.${_option.value}`;
      const userFields =
        nestedFields?.filter(
          (field) => field.objectType?.toString() !== ObjectType.Form,
        ) ?? [];

      return isUserDataType
        ? {
            nestedOptions: [
              ...(templateType === TemplateObjectType.Automations
                ? userFields?.map((c) => ({
                    ...c,
                    label: c?.fieldLabel,
                    value: c?.fieldName,
                  }))
                : isMultiSelectUserField
                ? nativeMemberFieldsOptionsForMultiUserField
                : nativeMemberFieldsOptions
              ).map((_option, _index) => ({
                objectType: _field.objectType,
                id: `${_field.fieldId}_${_option.id}`,
                indexString: `${index}.${_index}`,
                label: _option.label,
                value: `${parentFEValue} → ${_option.label}`,
                backendValue:
                  templateType === TemplateObjectType.Automations
                    ? `${_field.fieldName}.${_option.value}`
                    : _backendValue(_option),
                realTimeValue: getRealTimeValueForNestedField(
                  _field,
                  _option.value,
                ),
              })),
              ...(templateType === TemplateObjectType.Automations
                ? []
                : isMultiSelectUserField
                ? []
                : teamMemberFields ?? []
              )?.map((_option, _index) => {
                const frontendValue = `${parentFEValue}.${_option.fieldName}`;
                const backendValue = _option.customField
                  ? `${parentBEValue}.customField.${_option.fieldId}`
                  : `${parentBEValue}.${_option.fieldName}`;

                const parsedOptionLabel = tryGetTextFromHTML(
                  _option.fieldLabel,
                );

                return {
                  objectType: _field.objectType,
                  id: `${_field.fieldId}_${_option.fieldId}`,
                  indexString: `${index}.${_index}`,
                  label: parsedOptionLabel,
                  value:
                    templateType === TemplateObjectType.Automations
                      ? `${_field.fieldLabel} → ${parsedOptionLabel}`
                      : frontendValue,
                  backendValue,
                  realTimeValue: getRealTimeValueForNestedCustomField(
                    _field,
                    _option,
                  ),
                  /**
                   * below code is to support multiple nesting of
                   * team member fields.
                   */
                  // ...getUserFieldOptions(
                  //   _option,
                  //   _index,
                  //   frontendValue,
                  //   backendValue,
                  // ),
                };
              }),
            ],
          }
        : {};
    },
    [
      templateType,
      nestedFields,
      nativeMemberFieldsOptionsForMultiUserField,
      nativeMemberFieldsOptions,
      teamMemberFields,
      getRealTimeValueForNestedField,
      getRealTimeValueForNestedCustomField,
    ],
  );

  const fieldMapper = useCallback(
    (_field, index) => {
      const value = (() => {
        if (_field.fieldName === 'annualizedRecurringRevenue') {
          return 'project.arr';
        }
        if (_field.fieldName === 'projectOwner') {
          return 'project.owner';
        }
        if (_field.fieldName === 'Status') {
          return 'project.status';
        }
        if (_field.fieldName === 'customerCompanyName') {
          return 'project.customer.companyName';
        }

        return `project.${_field.fieldName}`;
      })();
      const isFormObjectType =
        (_field?.objectType === ObjectType.Form &&
          _field?.metaData?.options !== undefined) ??
        false;
      const backendValue =
        _field.customField && _field.fieldName !== 'Status'
          ? `project.customField.${_field.fieldId}`
          : value;

      const frontendValue = `${
        _field.fieldName === 'annualizedRecurringRevenue'
          ? 'ARR'
          : _field.fieldName === 'projectOwner'
          ? 'Owner'
          : _field.fieldName === 'Status'
          ? 'Status'
          : _field.fieldLabel
      }`;

      const _value =
        templateType === TemplateObjectType.Automations
          ? tryGetTextFromHTML(_field.fieldLabel)
          : frontendValue;

      const _beValue =
        templateType === TemplateObjectType.Automations
          ? _field.fieldName
          : backendValue;

      return {
        id: _field.fieldId,
        indexString: `${index}`,
        label: tryGetTextFromHTML(_field.fieldLabel),
        value: _value,
        backendValue: _beValue,
        objectType: _field.objectType,
        fieldDataType: _field.fieldDataType,
        fieldName: _field.fieldName,
        fieldType: filterableFieldType(_field as Field),
        realTimeValue: getRealTimeValue(_field),
        customField: _field.customField ?? false,
        placeholderField: false,
        metaData: {
          required: true,
          ..._field.metaData,
        },
        ...(!isFormObjectType
          ? getUserFieldOptions(_field, index, _value, _beValue)
          : getFormFieldOptions(_field, index, _value, _beValue)),
      };
    },
    [getRealTimeValue, getUserFieldOptions, getFormFieldOptions, templateType],
  );

  const projectFieldsOptions = useMemo(() => {
    const resp = (
      templateType === TemplateObjectType.Automations
        ? fields
        : projectFields?.filter(
            ({ fieldName, customField }) =>
              customField ||
              [
                'projectId',
                'projectName',
                'projectOwner',
                'dueDate',
                'startDate',
                'projectFee',
                'annualizedRecurringRevenue',
                'budgetedHours',
              ].includes(fieldName),
          )
    )

      ?.concat(
        templateType === TemplateObjectType.Automations
          ? []
          : (calculatedProjectFieldOptions as any),
      )
      .filter((e) => !!e?.fieldDataType && !!e?.fieldType)
      ?.map(fieldMapper);

    return resp;
  }, [
    calculatedProjectFieldOptions,
    fieldMapper,
    fields,
    projectFields,
    templateType,
  ]);

  if (templateType === TemplateObjectType.Automations) {
    return projectFieldsOptions;
  }

  const fieldOptionsWithPlaceholders = projectFieldsOptions?.concat(
    targetPlaceholders.map((_placeholder, index) => {
      const value = _placeholder.teamName;
      const backendValue = `project.placeholder.${_placeholder.teamId}`;
      const actualIndex = projectFieldsOptions.length + index;

      return {
        id: _placeholder.teamId,
        indexString: `${actualIndex}`,
        label: _placeholder.teamName,
        value,
        backendValue,
        objectType: translate('placeholders'),
        fieldDataType: FieldDataType.User,
        fieldType: FieldType.MultiUser,
        fieldName: _placeholder.teamName,
        realTimeValue: null,
        customField: false,
        placeholderField: true,
        metaData: {
          required: true,
        },
        nestedOptions: [
          ...nativeMemberFieldsOptions.map((_option, _index) => ({
            id: `${_placeholder.teamId}_${_option.id}`,
            indexString: `${actualIndex}.${_index}`,
            label: _option.label,
            value: `${value} → ${_option.label}`,
            objectType: translate('placeholders'),

            backendValue:
              _option.value === 'role'
                ? `${backendValue}.role.roleName`
                : `${backendValue}.${_option.value}`,
            realTimeValue: getRealTimeValueForPlaceholderNativeField(
              _placeholder,
              _option.value,
            ),
          })),
          ...(teamMemberFields ?? [])?.map((_option, _index) => ({
            id: `${_placeholder.teamId}_${_option.fieldId}`,
            indexString: `${actualIndex}.${_index}`,
            label: tryGetTextFromHTML(_option.fieldLabel),
            objectType: translate('placeholders'),
            value: `${value}.${_option.fieldName}`,
            backendValue: _option.customField
              ? `${backendValue}.customField.${_option.fieldId}`
              : `${backendValue}.${_option.fieldName}`,
            realTimeValue: getRealTimeValueForPlaceholderCustomField(
              _placeholder,
              _option,
            ),
          })),
        ],
      };
    }),
  );

  /**
   * sorts the options by fields with values at the top
   * and fields without values at the end of the list.
   */
  if (projectId && !realTimeVariablesResponse.isLoading) {
    return [
      ...(fieldOptionsWithPlaceholders?.filter(
        (_option) => _option.realTimeValue !== undefined,
      ) ?? []),
      ...(fieldOptionsWithPlaceholders?.filter(
        (_option) => _option.realTimeValue === undefined,
      ) ?? []),
    ].map((_option, index) => ({
      ..._option,
      indexString: `${index}`,
    }));
  }

  return fieldOptionsWithPlaceholders;
};

export const useProjectFieldsForVariables = ({
  templateType,
}: {
  templateType?: TemplateObjectType;
}): {
  fields: FilterableField[];
} => {
  const transformFieldName = (field): string => {
    const backendValue = field.backendValue.replace('project.', '');
    const fieldName = field.customField
      ? backendValue === 'status'
        ? `conditionalCustomField.${field.id}`
        : backendValue.replace('customField', 'conditionalCustomField')
      : backendValue;

    return field.placeholderField ? `${fieldName}.userId` : fieldName;
  };

  const variableOptions = useVariablesMentionData({ templateType });
  const { data: users } = useNativeUsersQuery();

  return useMemo(
    () => ({
      fields: variableOptions?.map((field) => {
        return {
          fieldLabel: field.label,
          fieldId: parseInt(field.indexString, 10),
          fieldName: transformFieldName(field),
          metaData:
            field.fieldDataType === FieldDataType.User
              ? {
                  ...field.metaData,
                  users: {
                    by_user_id: fromPairs(
                      (users ?? []).map((x) => [x.userId, x]),
                    ),
                    active_user_ids: (users ?? []).map((x) => x.userId),
                    non_external_user_ids: (users ?? []).map((x) => x.userId),
                    by_project_id: {},
                  },
                }
              : field.metaData,
          type: field.fieldType,
        };
      }) as FilterableField[],
    }),
    [variableOptions, users],
  );
};
